import { useState, useEffect } from 'react';
import persistProfilePref from '../utils/persistProfilePref';

/**
 * useAppState — top-level App-wide UI/encounter state (Feb 26, 2026)
 *
 * Extracted from `App.js` as part of the refactor pass. These are simple
 * `useState` declarations that don't belong to any feature hook
 * (`useTemplates`, `useHistory`, `useDocumentOutput`, etc.) but ARE
 * read/written across many components. Bundling them keeps `App.js` lean
 * without creating yet another large hook.
 *
 * If a future move wants to lift any of these into a domain-specific hook
 * (e.g., `virtualModality` → `useEncounterMeta`), feel free to relocate.
 */
export const useAppState = () => {
  // Virtual-encounter modality. Persisted per device + per profile. Default 'video'.
  // Mar 1, 2026 — also reads `writeright_last_virtual_modality` (the profile-synced
  // value) so a fresh sign-in on another device restores the provider's habitual modality.
  const [virtualModality, setVirtualModality] = useState(
    () => localStorage.getItem('writeright_last_virtual_modality')
      || localStorage.getItem('writeright_virtual_modality')
      || 'video'
  );
  // Persist on change — profile + local. Debounced inside persistProfilePref.
  useEffect(() => {
    if (!virtualModality) return;
    try {
      localStorage.setItem('writeright_virtual_modality', virtualModality);
      localStorage.setItem('writeright_last_virtual_modality', virtualModality);
    } catch (_) { /* ignore */ }
    persistProfilePref({ last_virtual_modality: virtualModality });
  }, [virtualModality]);

  // Date-of-service override + time-of-service override. Empty = use today/now.
  const [dosOverride, setDosOverride] = useState('');
  const [dosTimeOverride, setDosTimeOverride] = useState('');

  // Feb 28, 2026 (v6.8.48) — Late-Entry flag.
  // When the provider is catching up on old notes, this checkbox marks
  // the note as a Late Entry. The signature block stamps the note
  // with "*** LATE ENTRY ***" and the Note Updated line includes the
  // late-entry marker so the EMR signature audit captures it
  // verbatim. Reset on Next Patient via the writeright:next-patient event
  // (see clearAll in useFormActions).
  const [lateEntry, setLateEntry] = useState(false);

  // Free-text reason-for-visit (overrides chief-complaint quick-picks).
  const [customReasonForVisit, setCustomReasonForVisit] = useState('');

  // Bilingual output toggle. 'en' (default) | 'es'.
  const [language, setLanguage] = useState('en');

  // Shared between useFormActions (write) and useAutosaveSession (read).
  // Identifies the in-flight draft so autosave doesn't create dupes.
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [autoSavedDraft, setAutoSavedDraft] = useState(false);

  return {
    virtualModality, setVirtualModality,
    dosOverride, setDosOverride,
    dosTimeOverride, setDosTimeOverride,
    // v6.8.48 — Late-entry checkbox state
    lateEntry, setLateEntry,
    customReasonForVisit, setCustomReasonForVisit,
    language, setLanguage,
    currentDraftId, setCurrentDraftId,
    autoSavedDraft, setAutoSavedDraft,
  };
};

export default useAppState;
